import {
  apiRequest,
  makeBodilessQuery,
  makeUnauthenticatedJSONQuery,
} from './base-request';

const _postLogin = makeUnauthenticatedJSONQuery('login_check');
const _postRegister = makeUnauthenticatedJSONQuery(
  'register?redirectUri={{redirectUri}}&plan={{plan}}',
);
const _postRegisterAppSumo = makeUnauthenticatedJSONQuery(
  'integrations/appsumo/activate/{{appSumoToken}}',
);
const _getAccountActivation = makeBodilessQuery('activate/{{activationToken}}');
const _resetPasswordRequest = makeUnauthenticatedJSONQuery(
  'resetPasswordRequest?redirectUri={{redirectUri}}',
  'POST',
);
const _validateResetPasswordToken = makeBodilessQuery(
  'resetPassword/validateToken',
);

const _resetPassword = makeUnauthenticatedJSONQuery('resetPassword', 'POST');
const _refreshToken = makeUnauthenticatedJSONQuery('token/refresh', 'POST');

/**
 * Acquire JWT and user data via username & password
 */
export const postLogin = (username: string, password: string) =>
  _postLogin({
    _username: username,
    _password: password,
  });

type RegisterFormData = {
  email: string;
  plainPassword: string;
  firstName: string;
  lastName: string;
  termsOfService: boolean;
  privacyPolicy: boolean;
  redirectUri?: string;
  plan?: string | null;
};

/**
 * Create user account
 */
export const postRegister = (registerFormData: RegisterFormData) =>
  _postRegister(registerFormData);

/**
 * Create user account via AppSumo integration
 */
export const postRegisterAppSumo = (
  activationToken: string,
  registerFormData: RegisterFormData,
) =>
  _postRegisterAppSumo({
    appSumoToken: activationToken,
    ...registerFormData,
  });

export const postOauthLogin = <T>(body: T, queryParams: string) => {
  const path = `oauth2/login?${queryParams}`;
  return apiRequest('POST', path, undefined, undefined, {
    credentials: 'include',
    body: JSON.stringify(body),
  });
};

export const postOauthConsent = <T>(body: T, queryParams: string) => {
  const path = `oauth2/consent?${queryParams}`;
  return apiRequest('POST', path, undefined, undefined, {
    credentials: 'include',
    body: JSON.stringify(body),
  });
};

/**
 * Activate user account with a valid token
 */
export const getAccountActivation = (activationToken: string) =>
  _getAccountActivation(undefined, undefined, {
    activationToken,
  });

/**
 * Send password reset request
 */
export const resetPasswordRequest = (email: string, redirectUri: string) =>
  _resetPasswordRequest({
    email,
    sendEmail: true,
    redirectUri,
  });

/**
 * Validate reset password token
 */
export const validateResetPasswordToken = (resetPasswordToken: string) =>
  _validateResetPasswordToken(undefined, undefined, {
    resetPasswordToken,
  });

/**
 * Send password reset request
 */
export const resetPassword = (resetPasswordFormData: {
  plainPassword: string;
  plainPasswordRepeat: string;
  activationToken: string | null;
  resetPasswordToken: string | null;
}) => _resetPassword(resetPasswordFormData);

/**
 * Send refresh token request
 */
export const refreshToken = () => {
  const refresh_token = JSON.parse(
    localStorage.getItem('cms.user') || '{}',
  )?.refresh_token;
  return _refreshToken({ refresh_token });
};
